import { PlanTrailActionContext } from "./types";
import { Route, RouteFactory } from "../../../../../utils/models/Route";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { MarkerFeature, MarkerGeometry, MarkerProperties } from "../../../../../utils/models/MarkerFeature";
import { snapPoints } from "../../../../../utils/models/Graphooper";
import { hiikerElevation } from "../../../../../utils/helpers/HiikerElevationApiHelper";
import { generateID } from "../../../../../utils/helpers/IDGenerator";
import { i18n } from "../../../../../i18n";
import {
  nearestOnTrail,
  pointAtDistance,
  cumulativeDistances,
  routeAnchorCoord,
  dayNumberForDistance,
  ON_TRAIL_M,
  MIN_DAYS,
  MAX_DAYS,
  DayBoundary,
  MeasuredLine,
} from "../../../utils/trailLine";
import { candidateGateDistances, cropDetourAtTrail, shortestRoutedCandidate } from "../../../services/navigable-gate";
import { haversineMeters, orderedDayStages, detectStageConnectors, CONNECTOR_COINCIDENT_M, StageConnector } from "../../../services/stage-connectors";
import { bridgeCoordinate } from "../../../services/off-network-bridge";
import type { StageDestination } from "../../../services/stage-destinations";
import {
  dayFlagLine,
  placeDayFlag,
  routeLine,
  wholeDayLine,
  splitIntoDayParts,
  evenDayBoundaries,
  nearestAlongLine,
  daySpacingProblem,
  MIN_DAY_M,
  DayJunction,
} from "../../../services/day-flags";
import { splitIntoDays, mergeDays, captureDayKeyedState, applyDayKeyedState } from "../../../services/split-into-days";
import { RouteSnapshotsCommand } from "../../undo/commands/RouteSnapshotsCommand";
import {
  nightsAlong,
  nightTargets,
  planNights,
  walkingSeconds,
  walkLine,
  NightStop,
  Pace,
  StayKind,
  STAY_KINDS,
  STAY_SEARCH_BUFFER_M,
} from "../../../services/night-planner";
import { stopHighlightIds } from "../../../services/destination-highlights";
import { addressKey, placesAlongLine, rememberedAddresses } from "../../../services/nearby-places";
import { PlacePick, pickCoordinates, pickDestination } from "../../../services/place-picks";
import { ApplyDayCountCommand } from "../../undo/commands/ApplyDayCountCommand";

// Translated when called (never at module load) so names follow the page's locale.
const t = (key: string, named: Record<string, unknown> = {}): string => i18n.global.t(key, named);

const isMainRoute = (route: Route): boolean => (route.route_style ?? 'route') === 'route';

function markerAtCoord(coord: number[], isSnap: boolean): MarkerFeature {
  return new MarkerFeature(
    new MarkerGeometry([...coord]),
    new MarkerProperties(generateID(), isSnap, Date.now().toString(), coord[2] ?? 0)
  );
}

/**
 * The way from the line out to `targetCoord` that the routing engine can
 * actually walk, rejoining the line by the shortest path. The straight-line
 * nearest point is often unreachable directly (a river, a fence, a crag between
 * it and the drop), so several spaced-out points along the line are routed in
 * parallel — see services/navigable-gate.ts. Null when none could be routed.
 */
async function navigableGate(
  trail: MeasuredLine,
  targetCoord: number[],
  nearestDist: number,
  minDist: number,
  maxDist: number,
  targetMarker: MarkerFeature,
  routePreference: string
): Promise<{ distanceM: number; point: number[]; line: LineStringFeature } | null> {
  const distances = candidateGateDistances(trail, targetCoord, minDist, maxDist);
  if (!distances.includes(nearestDist)) distances.unshift(nearestDist);

  const routed = await Promise.all(distances.map(async (distanceM) => {
    const point = pointAtDistance(trail.coordinates, trail.cumDist, distanceM);
    try {
      const routed = await snapPoints(markerAtCoord(point, true), targetMarker, routePreference);
      // The way back often reaches the line before this point and then runs
      // along it — rejoin where it first meets the line instead.
      const cropped = cropDetourAtTrail(trail, routed.lineStringGeometry.coordinates, minDist, maxDist);
      const line = cropped ? LineStringFeature.coordsOnlyFeature(cropped.coordinates) : routed;
      if (cropped) line.lineStringProperties.profile = routed.lineStringProperties.profile;
      const lineCoords = line.lineStringGeometry.coordinates;
      const routedM = lineCoords.length > 1 ? cumulativeDistances(lineCoords)[lineCoords.length - 1] : null;
      return cropped
        ? { distanceM: cropped.distanceM, point: cropped.point, line, routedM }
        : { distanceM, point, line, routedM };
    } catch (err) {
      console.warn('[DAY_FLAGS] no route from a rejoin candidate:', err);
      return { distanceM, point, line: null, routedM: null };
    }
  }));

  const best = shortestRoutedCandidate(routed);
  return best && best.line ? { distanceM: best.distanceM, point: best.point, line: best.line } : null;
}

/**
 * Where on `trail` (between minDist and maxDist) a flag dropped at
 * `targetCoord` belongs, and the detour out to the drop point when it's
 * further than ON_TRAIL_M from the line.
 *
 * `forceOffTrail` bypasses the ON_TRAIL_M allowance. That allowance exists to
 * treat a near-miss drop as "the user meant the line", but dropping onto a
 * POI/waypoint/highlight is a deliberate choice of a specific place — for an
 * item within 30m of the path (a hotel just off it, say) the allowance would
 * otherwise swallow the attach and leave the night on the line beside it.
 */
export async function gateOntoLine(
  trail: MeasuredLine,
  targetCoord: number[],
  minDist: number,
  maxDist: number,
  isSnap: boolean,
  routePreference: string,
  forceOffTrail: boolean = false
): Promise<DayBoundary> {
  const gate = nearestAlongLine(trail, targetCoord);
  let clampedDist = Math.min(Math.max(gate.distanceM, minDist), maxDist);
  let gatePoint = clampedDist === gate.distanceM
    ? gate.point
    : pointAtDistance(trail.coordinates, trail.cumDist, clampedDist);

  const offTrailM = haversineMeters(targetCoord, gatePoint);
  let offTrailPoint: number[] | null = null;
  let detourLine: LineStringFeature | null = null;

  // `offTrailM > 0` still guards the forced case: an item sitting exactly on
  // the line needs no detour, and a zero-length one would be degenerate.
  if (offTrailM > ON_TRAIL_M || (forceOffTrail && offTrailM > 0)) {
    offTrailPoint = [...targetCoord];
    const gateMarker = markerAtCoord(gatePoint, isSnap);
    const targetMarker = markerAtCoord([targetCoord[0], targetCoord[1], 0], isSnap);
    const navigable = isSnap
      ? await navigableGate(trail, targetCoord, clampedDist, minDist, maxDist, targetMarker, routePreference)
      : null;
    if (navigable) {
      clampedDist = navigable.distanceM;
      gatePoint = navigable.point;
      detourLine = navigable.line;
    } else {
      // Straight-line routing chosen, or GraphHopper genuinely has no route
      // from any rejoin point to the drop point (e.g. a POI off any routable
      // way) — fall back to a straight detour rather than abandoning the drop.
      detourLine = await hiikerElevation(gateMarker, targetMarker);
    }
    detourLine.lineStringProperties.id = generateID();

    // GraphHopper ends a routed detour on the nearest routable WAY, which for
    // an item set back from the path (a hotel entrance, say) can be a good
    // 15m short of the item itself. The day's end marker comes from this
    // line's last coordinate, and resolveAttachedDestinations prunes any
    // destination more than DESTINATION_ATTACHED_THRESHOLD_M (15m) from its
    // item — so bridge the remainder, the same way polylines.ts extends an
    // unreachable pinned endpoint.
    if (forceOffTrail) {
      const coords = detourLine.lineStringGeometry.coordinates;
      const last = coords[coords.length - 1];
      if (!last || haversineMeters(last, targetCoord) > 0) {
        // Elevation inherited from the routed point this joins onto — the item
        // has none of its own, and a 0 would read as phantom descent.
        coords.push(bridgeCoordinate(targetCoord[0], targetCoord[1], targetCoord[2], last));
      }
    }
  }

  return { distanceM: clampedDist, offTrailPoint, detourLine };
}

/**
 * A leg from `from` to `to`, routed when routing is on (straight when it's off
 * or there's no route), that really starts and ends at those two points.
 */
async function legBetween(from: number[], to: number[], isSnap: boolean, routePreference: string): Promise<LineStringFeature> {
  const start = markerAtCoord(from, isSnap);
  const end = markerAtCoord([to[0], to[1], to[2] ?? 0], isSnap);
  const line = isSnap
    ? await snapPoints(start, end, routePreference).catch(() => hiikerElevation(start, end))
    : await hiikerElevation(start, end);
  line.lineStringProperties.id = generateID();
  // A routed leg starts and ends on the nearest way, which can be short of
  // either point — bridge the rest so the two days really meet.
  const coords = line.lineStringGeometry.coordinates;
  const first = coords[0];
  if (first && haversineMeters(first, from) > 0) coords.unshift(bridgeCoordinate(from[0], from[1], from[2], first));
  const last = coords[coords.length - 1];
  if (last && haversineMeters(last, to) > 0) coords.push(bridgeCoordinate(to[0], to[1], to[2], last));
  return line;
}

/**
 * Joins day `prevKey` to day `nextKey` where they don't meet — a gap in
 * imported or official data, or days the user separated. The earlier day
 * walks on to where the later one starts, along a short new leg; neither
 * day's existing line changes. No undo entry — callers push their own.
 * Returns false when either day has no point to join.
 */
export async function bridgeDays(state: any, prevKey: string, nextKey: string): Promise<boolean> {
  const prev = state.routes[prevKey];
  const next = state.routes[nextKey];
  const from = prev?.markers?.[prev.markers.length - 1]?.markerGeometry?.coordinates;
  const to = next?.markers?.[0]?.markerGeometry?.coordinates;
  if (!from || !to) return false;

  const connectorId = `${prevKey}::${nextKey}`;
  state.disconnectedStageIds = (state.disconnectedStageIds || []).filter((id: string) => id !== connectorId);
  if (haversineMeters(from, to) <= CONNECTOR_COINCIDENT_M) return true;

  const leg = await legBetween(from, to, state.isSnap, state.routePreference);
  prev.markers.push(markerAtCoord([to[0], to[1], to[2] ?? 0], state.isSnap));
  prev.lineStrings.push(leg);
  prev.updated_at = new Date();
  state._statsVersion++;
  return true;
}

/**
 * Joined days whose ends don't quite meet — within the join distance, but
 * apart — get a short leg between them (bridgeDays), so moving, merging or
 * re-spacing their flag never turns the gap into a straight jump inside a day.
 */
async function closeJoinGaps(state: any, pairs: Array<{ prevKey: string; nextKey: string }>): Promise<void> {
  for (const { prevKey, nextKey } of pairs) {
    const prev = state.routes[prevKey];
    const next = state.routes[nextKey];
    const from = prev?.markers?.[prev.markers.length - 1]?.markerGeometry?.coordinates;
    const to = next?.markers?.[0]?.markerGeometry?.coordinates;
    if (from && to && haversineMeters(from, to) > CONNECTOR_COINCIDENT_M) await bridgeDays(state, prevKey, nextKey);
  }
}

/** Length of a day's main line, in metres (0 when it has none). */
function dayLengthM(routes: Route[]): number {
  return routes.reduce((sum, route) => {
    const line = routeLine(route);
    return sum + (line ? line.markerDistM[line.markerDistM.length - 1] : 0);
  }, 0);
}

function mainRoutesOfDay(state: any, day: number): Route[] {
  return (Object.values(state.routes) as Route[])
    .filter(route => isMainRoute(route) && (route.day ?? 1) === day)
    .sort((a, b) => a.position - b.position);
}

/** Pairs of consecutive days whose shared flag joins them, in day order. */
function joinedDayPairs(state: any): Array<{ day: number; prevKey: string; nextKey: string }> {
  const connectors = detectStageConnectors(state.routes, undefined, state.disconnectedStageIds);
  return connectors
    .map(connector => ({ day: connector.stageNumber, prevKey: connector.prevRouteKey, nextKey: connector.nextRouteKey }))
    .sort((a, b) => a.day - b.day);
}

/** Snapshot of everything a change to the days can touch, for ApplyDayCountCommand. */
function dayPlanSnapshot(state: any) {
  return {
    routes: JSON.parse(JSON.stringify(state.routes)),
    activeRouteKey: state.activeRouteKey,
    dayInfo: JSON.parse(JSON.stringify(state.dayInfo)),
    stageDestinations: JSON.parse(JSON.stringify(state.stageDestinations)),
  };
}

function pushDayPlanChange(state: any, before: ReturnType<typeof dayPlanSnapshot>) {
  const after = dayPlanSnapshot(state);
  state.undoManager.push(new ApplyDayCountCommand({
    routesBefore: before.routes,
    routesAfter: after.routes,
    activeRouteKeyBefore: before.activeRouteKey,
    activeRouteKeyAfter: after.activeRouteKey,
    dayInfoBefore: before.dayInfo,
    dayInfoAfter: after.dayInfo,
    stageDestinationsBefore: before.stageDestinations,
    stageDestinationsAfter: after.stageDestinations,
  }));
}

export type DayFlagMoveResult = 'moved' | 'needs-detour' | 'unchanged';

export const dayFlagActions = {
  /**
   * Joins every day to the next where they don't meet (see bridgeDays), as
   * one undoable change — what "Join days" next to the days toolbar's note
   * does when spacing is blocked by a gap.
   */
  async joinAllDays({ state, dispatch }: PlanTrailActionContext): Promise<boolean> {
    const stages = orderedDayStages(state.routes);
    const joined = new Set(detectStageConnectors(state.routes, undefined, state.disconnectedStageIds).map(c => c.prevRouteKey));
    const gaps = stages.slice(0, -1)
      .map((stage, i) => ({ prevKey: stage.lastKey, nextKey: stages[i + 1].firstKey }))
      .filter(gap => !joined.has(gap.prevKey));
    if (gaps.length === 0) return false;

    // Adding a leg edits the earlier day — referenced hikes get the same
    // warning that gates any other edit to them.
    const win = window as any;
    const blocked = gaps.map(gap => state.routes[gap.prevKey])
      .find(route => route?.isReference && typeof win.canEditReference === 'function' && !win.canEditReference(route));
    if (blocked) {
      if (typeof win.checkReferenceEditWarning === 'function') win.checkReferenceEditWarning(blocked);
      return false;
    }

    const before = dayPlanSnapshot(state);
    for (const gap of gaps) await bridgeDays(state, gap.prevKey, gap.nextKey);
    pushDayPlanChange(state, before);
    await dispatch('updateRouteAndMarkers');
    return true;
  },

  /**
   * Moves the flag between day `prev` and day `next` to `targetCoord`. The
   * flag slides along the line the two days walk — the line itself never
   * changes. Dropped off the line, the night goes off the trail: the earlier
   * day walks out to it along a routed detour and the later day walks back.
   *
   * A plain drop off the line returns 'needs-detour' without changing
   * anything unless `allowDetour` is set, so the caller can ask first. A drop
   * onto a POI/waypoint/highlight (`destination`) is deliberate and goes
   * straight ahead, recording the place as that day's destination.
   */
  async moveDayFlag(
    { state, commit, dispatch }: PlanTrailActionContext,
    payload: {
      prevRouteKey: string;
      nextRouteKey: string;
      targetCoord: [number, number];
      destination?: StageDestination | null;
      allowDetour?: boolean;
    }
  ): Promise<DayFlagMoveResult> {
    const prev = state.routes[payload.prevRouteKey];
    const next = state.routes[payload.nextRouteKey];
    if (!prev || !next) return 'unchanged';
    const { targetCoord, destination } = payload;

    // Where the drop lands on the line decides whether to ask first — worked
    // out before anything changes, so a "no" leaves the days untouched.
    const where = (line: NonNullable<ReturnType<typeof dayFlagLine>>) => {
      const total = line.trail.cumDist[line.trail.cumDist.length - 1] ?? 0;
      const minDist = Math.min(MIN_DAY_M, total / 2);
      const maxDist = Math.max(total - MIN_DAY_M, total / 2);
      const nearest = nearestAlongLine(line.trail, targetCoord);
      const onLineDist = Math.min(Math.max(nearest.distanceM, minDist), maxDist);
      const onLinePoint = pointAtDistance(line.trail.coordinates, line.trail.cumDist, onLineDist);
      return { minDist, maxDist, onLineDist, offLine: haversineMeters(targetCoord, onLinePoint) > ON_TRAIL_M };
    };
    const firstLook = dayFlagLine(prev, next);
    if (!firstLook) return 'unchanged';
    if (where(firstLook).offLine && !destination && !payload.allowDetour) return 'needs-detour';

    const prevBefore: Route = JSON.parse(JSON.stringify(prev));
    const nextBefore: Route = JSON.parse(JSON.stringify(next));

    // Days joined across a small gap get a leg between them first, so the
    // gap doesn't end up as a straight jump inside one of them.
    await closeJoinGaps(state, [{ prevKey: payload.prevRouteKey, nextKey: payload.nextRouteKey }]);
    const line = dayFlagLine(prev, next) ?? firstLook;
    const { minDist, maxDist, onLineDist, offLine } = where(line);

    const boundary = offLine || destination
      ? await gateOntoLine(line.trail, targetCoord, minDist, maxDist, state.isSnap, state.routePreference, !!destination)
      : { distanceM: onLineDist, offTrailPoint: null, detourLine: null };

    const parts = placeDayFlag(line, boundary.distanceM, state.isSnap, boundary.detourLine);

    prev.markers = parts.prev.markers;
    prev.lineStrings = parts.prev.lineStrings;
    next.markers = parts.next.markers;
    next.lineStrings = parts.next.lineStrings;
    prev.updated_at = new Date();
    next.updated_at = new Date();

    state.undoManager.push(new RouteSnapshotsCommand('MOVE_DAY_FLAG', [
      { routeKey: payload.prevRouteKey, before: prevBefore, after: JSON.parse(JSON.stringify(prev)) },
      { routeKey: payload.nextRouteKey, before: nextBefore, after: JSON.parse(JSON.stringify(next)) },
    ]));

    // Recorded after the rebuild, so the coordinates stored are the item's
    // own — the day now ends there via the detour, which keeps it inside
    // DESTINATION_ATTACHED_THRESHOLD_M and stops it being pruned as stale.
    if (destination) {
      const previous = state.stageDestinations?.[prev.day ?? 1] ?? null;
      commit('setStageDestination', {
        dayNumber: prev.day ?? 1,
        destination: { ...destination, coordinates: [targetCoord[0], targetCoord[1]] },
      });
      dispatch('addHighlightForDestination', { destination, dayNumber: prev.day ?? 1 });
      await dispatch('dropStopHighlight', previous);
    }

    state._statsVersion++;
    return 'moved';
  },

  /**
   * Adds a night: the longest day is split in two at its halfway point, and
   * every later day moves along one. The new boundary is a flag like any
   * other, so it can be dragged wherever the night should really be.
   */
  async addDay({ state, getters, commit, dispatch }: PlanTrailActionContext): Promise<boolean> {
    const stages = orderedDayStages(state.routes);
    if (stages.length === 0 || stages.length >= MAX_DAYS) return false;

    let longest: { day: number; length: number } | null = null;
    for (const stage of stages) {
      const length = dayLengthM(mainRoutesOfDay(state, stage.dayNumber));
      if (!longest || length > longest.length) longest = { day: stage.dayNumber, length };
    }
    if (!longest || longest.length <= MIN_DAY_M * 2) return false;

    // The route holding the day's halfway point, and how far into it that is.
    let remaining = longest.length / 2;
    let split: { route: Route; line: NonNullable<ReturnType<typeof routeLine>>; atM: number } | null = null;
    for (const route of mainRoutesOfDay(state, longest.day)) {
      const line = routeLine(route);
      if (!line) continue;
      const length = line.markerDistM[line.markerDistM.length - 1];
      if (remaining <= length) {
        split = { route, line, atM: remaining };
        break;
      }
      remaining -= length;
    }
    if (!split) return false;

    const before = dayPlanSnapshot(state);
    const routeKey = Object.keys(state.routes).find(key => state.routes[key] === split!.route) ?? split.route.id;
    const route = state.routes[routeKey];
    const parts = placeDayFlag(split.line, split.atM, state.isSnap);

    route.markers = parts.prev.markers;
    route.lineStrings = parts.prev.lineStrings;
    route.updated_at = new Date();

    const day = route.day ?? 1;
    applyDayKeyedState(state, splitIntoDays(captureDayKeyedState(state), state.routes, routeKey, day));

    const newRoute = RouteFactory.createNew(
      t('plan_trail.route_operations.part_two_name', { name: route.name }), route.position + 1, day + 1
    );
    newRoute.markers = parts.next.markers;
    newRoute.lineStrings = parts.next.lineStrings;
    newRoute.route_style = route.route_style;
    commit('addRoute', newRoute);

    const order: string[] = [];
    for (const id of (getters['routesArray'] as Route[]).map(r => r.id).filter(id => id !== newRoute.id)) {
      order.push(id);
      if (id === routeKey) order.push(newRoute.id);
    }
    commit('updateRoutePositions', order);

    state._statsVersion++;
    pushDayPlanChange(state, before);
    await dispatch('addAllRoutesToMap');
    return true;
  },

  /**
   * Takes a night out: of the days joined by a shared flag, the two that make
   * the shortest walk together become one day, and every later day moves back
   * one. A flag the user pinned to a place is only removed when every flag is.
   */
  async removeDay({ state, dispatch }: PlanTrailActionContext): Promise<boolean> {
    const pairs = joinedDayPairs(state);
    if (pairs.length === 0) return false;

    const lengthOfDay = (day: number) => dayLengthM(mainRoutesOfDay(state, day));
    const pinned = (day: number) => !!state.stageDestinations[day];
    const candidates = pairs.some(pair => !pinned(pair.day)) ? pairs.filter(pair => !pinned(pair.day)) : pairs;
    const pick = candidates.reduce((best, pair) =>
      lengthOfDay(pair.day) + lengthOfDay(pair.day + 1) < lengthOfDay(best.day) + lengthOfDay(best.day + 1) ? pair : best
    );

    const prev = state.routes[pick.prevKey];
    const next = state.routes[pick.nextKey];
    if (!prev || !next || !dayFlagLine(prev, next)) return false;

    const before = dayPlanSnapshot(state);
    await closeJoinGaps(state, [pick]);
    const line = dayFlagLine(prev, next);
    if (!line) {
      pushDayPlanChange(state, before);
      return false;
    }
    prev.markers = line.markers;
    prev.lineStrings = line.lineStrings;
    prev.updated_at = new Date();

    const mergedState = mergeDays(captureDayKeyedState(state), state.routes, pick.day);
    delete state.routes[pick.nextKey];
    delete mergedState.routeDays[pick.nextKey];
    applyDayKeyedState(state, mergedState);
    if (state.activeRouteKey === pick.nextKey) state.activeRouteKey = pick.prevKey;

    state._statsVersion++;
    pushDayPlanChange(state, before);
    await dispatch('addAllRoutesToMap');
    return true;
  },

  /**
   * Spaces the nights evenly along the whole walk, `dayCount` days in all
   * (the current number when omitted). Nights pinned to a place — a POI,
   * waypoint or highlight — stay where they are, along with any detour out to
   * them; the free ones are spread around them. The line itself never changes,
   * and control points along it are kept.
   *
   * Given `nightsM` (metres along the walk) instead, the nights go exactly
   * there and every pin is let go — the AI Planner's own plan.
   *
   * Needs one continuous line (see daySpacingProblem); returns false without
   * changing anything otherwise.
   */
  async spaceDaysEvenly(
    { state, commit, dispatch }: PlanTrailActionContext,
    request?: number | { nightsM: number[] }
  ): Promise<boolean> {
    if (daySpacingProblem(state.routes, state.disconnectedStageIds)) return false;

    const stages = orderedDayStages(state.routes);
    const nightsM = typeof request === 'object' && request ? request.nightsM.slice(0, MAX_DAYS - 1) : null;
    const rawDayCount = nightsM ? nightsM.length + 1 : (request as number | undefined);
    const dayCount = Math.min(MAX_DAYS, Math.max(MIN_DAYS, Math.round(rawDayCount ?? stages.length)));
    if (!wholeDayLine(stages.map(stage => stage.firstRoute))) return false;

    const before = dayPlanSnapshot(state);
    await closeJoinGaps(state, stages.slice(0, -1).map((stage, i) => ({ prevKey: stage.lastKey, nextKey: stages[i + 1].firstKey })));
    const dayRoutes = stages.map(stage => state.routes[stage.firstKey]);
    const line = wholeDayLine(dayRoutes);
    // Anything closed above stays undoable even if spacing can't go ahead.
    const giveUp = () => {
      pushDayPlanChange(state, before);
      return false;
    };
    if (!line) return giveUp();

    const total = line.trail.cumDist[line.trail.cumDist.length - 1] ?? 0;
    const pinned: Array<DayJunction & { destination: StageDestination }> = [];
    if (!nightsM) {
      line.junctions.forEach((junction, i) => {
        const destination = state.stageDestinations[stages[i].dayNumber];
        if (destination) pinned.push({ ...junction, destination });
      });
    }

    const boundaries = nightsM
      ? nightsM.map(distM => Math.min(Math.max(distM, MIN_DAY_M), total - MIN_DAY_M)).sort((a, b) => a - b)
      : evenDayBoundaries(total, pinned.map(p => p.distM), dayCount);
    const flags: DayJunction[] = boundaries.map(distM => pinned.find(p => p.distM === distM) ?? { distM, detour: null });
    const parts = splitIntoDayParts(line, flags, state.isSnap);
    if (!parts) return giveUp();

    // Reuse each day's existing route (keeping its name and id) for as many
    // days as there still are; add routes for new days, drop surplus ones.
    const mainKeys = stages.map(stage => stage.firstKey);
    const dayKeys: string[] = parts.map((part, i) => {
      const day = i + 1;
      const existingKey = mainKeys[i];
      const route = existingKey ? state.routes[existingKey] : RouteFactory.createNew(`Day ${day}`, 0, day);
      route.route_style = 'route';
      route.day = day;
      route.markers = part.markers;
      route.lineStrings = part.lineStrings;
      route.updated_at = new Date();
      if (!existingKey) commit('addRoute', route);
      return existingKey ?? route.id;
    });
    mainKeys.slice(parts.length).forEach(key => { delete state.routes[key]; });

    // Offshoots go with whichever day now runs past them; transport legs,
    // which can run between two off-trail points, just stay within range.
    const boundaryByDay: { [day: number]: DayBoundary } = {};
    boundaries.forEach((distanceM, i) => { boundaryByDay[i + 1] = { distanceM, offTrailPoint: null, detourLine: null }; });
    const offshoots = (Object.values(state.routes) as Route[]).filter(route => !isMainRoute(route));
    offshoots.forEach(route => {
      const anchor = route.route_style === 'transport' ? null : routeAnchorCoord(route);
      route.day = anchor
        ? dayNumberForDistance(nearestOnTrail(line.trail.coordinates, line.trail.cumDist, anchor[0], anchor[1]).distanceM, boundaryByDay, dayCount)
        : Math.min(Math.max(route.day ?? 1, 1), dayCount);
    });

    const order: string[] = [];
    dayKeys.forEach((key, i) => {
      order.push(key);
      offshoots
        .filter(route => route.day === i + 1)
        .sort((a, b) => a.position - b.position)
        .forEach(route => order.push(route.id));
    });
    commit('updateRoutePositions', order);

    // Pinned nights keep their place, under whichever day now ends there.
    const destinations: { [day: number]: StageDestination } = {};
    flags.forEach((flag, i) => {
      const pin = pinned.find(p => p === flag);
      if (pin) destinations[i + 1] = pin.destination;
    });
    state.stageDestinations = destinations;

    // Day names and descriptions describe where each day used to start and
    // end, so they go. Each day keeps its hike_day row id: the server matches
    // days by number, and dropping the ids would have it destroy every row
    // (and with them each day's offline maps and print pages).
    const dayInfo: { [day: number]: any } = {};
    for (let day = 1; day <= dayCount; day++) {
      const id = before.dayInfo[day]?.id;
      if (id != null) dayInfo[day] = { id, name: '', description: '' };
    }
    state.dayInfo = dayInfo;
    state.activeRouteKey = null;

    state._statsVersion++;
    pushDayPlanChange(state, before);
    await dispatch('addAllRoutesToMap');
    return true;
  },

  /**
   * The AI Planner's nights for `dayCount` days at `pace`: where each falls
   * when the walking time is shared out evenly, then — given a `stayKind` —
   * the place of that kind to stay nearest each (see planNights) — with how
   * long the walk is, for the days' lengths. A walk whose days don't join up,
   * or have several routes, is planned as its routes one after another
   * (applyNightPlan joins them). Null without a route to plan.
   */
  async previewNightPlan(
    { state }: PlanTrailActionContext,
    payload: { dayCount: number; pace: Pace; stayKind?: StayKind | null }
  ): Promise<{ stops: NightStop[]; totalM: number; line: MeasuredLine } | null> {
    const problem = daySpacingProblem(state.routes, state.disconnectedStageIds);
    if (problem?.kind === 'no-route') return null;
    const line = problem
      ? walkLine(state.routes)
      : wholeDayLine(orderedDayStages(state.routes).map(stage => stage.firstRoute))?.trail;
    if (!line || line.coordinates.length < 2) return null;
    const dayCount = Math.min(MAX_DAYS, Math.max(MIN_DAYS, Math.round(payload.dayCount)));
    const targets = nightTargets(line.cumDist, walkingSeconds(line, payload.pace), dayCount);
    const places = payload.stayKind
      ? await placesAlongLine(line.coordinates, STAY_KINDS[payload.stayKind], STAY_SEARCH_BUFFER_M)
      : [];
    return { stops: planNights(line, targets, places), totalM: line.cumDist[line.cumDist.length - 1], line };
  },

  /**
   * Lays the days out afresh as previewNightPlan planned them, whatever was
   * there before:
   *  - the old nights go, along with the highlights that were only there for
   *    them (see stopHighlightIds);
   *  - a walk that doesn't join up, or has days of several routes, is made
   *    into one route first (mergeAllRoutes, which routes across any gaps);
   *  - the nights go where the plan put them, and each with a place to stay
   *    is moved onto it (a detour when it's off the route) and pinned there,
   *    which makes the place that day's highlight.
   * Undoes in one step.
   */
  async applyNightPlan(
    { state, getters, commit, dispatch }: PlanTrailActionContext,
    payload: { stops: NightStop[] }
  ): Promise<boolean> {
    const problem = daySpacingProblem(state.routes, state.disconnectedStageIds);
    if (problem?.kind === 'no-route') return false;

    const mark = state.undoManager.mark();
    const before = dayPlanSnapshot(state);
    const highlightsBefore = JSON.parse(JSON.stringify(state.hikeHighlights));
    const squash = () => {
      const after = dayPlanSnapshot(state);
      state.undoManager.squashSince(mark, new ApplyDayCountCommand({
        routesBefore: before.routes,
        routesAfter: after.routes,
        activeRouteKeyBefore: before.activeRouteKey,
        activeRouteKeyAfter: after.activeRouteKey,
        dayInfoBefore: before.dayInfo,
        dayInfoAfter: after.dayInfo,
        stageDestinationsBefore: before.stageDestinations,
        stageDestinationsAfter: after.stageDestinations,
        hikeHighlightsBefore: highlightsBefore,
        hikeHighlightsAfter: JSON.parse(JSON.stringify(state.hikeHighlights)),
      }));
    };

    stopHighlightIds(state.stageDestinations, state.hikeHighlights).forEach(clientId => commit('removeHikeHighlight', clientId));
    if (problem) await dispatch('mergeAllRoutes');

    // The nights, found again on the walk as it is now (joining it up moves
    // things along a little).
    const line = wholeDayLine(orderedDayStages(state.routes).map(stage => stage.firstRoute))?.trail;
    const nightsM = line ? nightsAlong(line, payload.stops) : payload.stops.map(stop => stop.atM);
    if (!await dispatch('spaceDaysEvenly', { nightsM })) {
      squash();
      await dispatch('refreshHighlights');
      await dispatch('addAllRoutesToMap');
      return false;
    }

    for (const stop of payload.stops) {
      // Where they chose to stay, else the place found for them.
      const pick: PlacePick | null = stop.pick ?? (stop.place ? { kind: 'place', place: stop.place } : null);
      if (!pick) continue;
      const connector = ((getters?.stageConnectors ?? []) as StageConnector[])
        .find(candidate => state.routes[candidate.prevRouteKey]?.day === stop.night);
      if (!connector) continue;
      const destination = pick.kind === 'google'
        ? await dispatch('addHighlightForPick', { pick, dayNumber: stop.night })
        : pickDestination(pick);
      const targetCoord = pickCoordinates(pick);
      if (pick.kind === 'address') rememberedAddresses.set(addressKey(targetCoord), pick.name);
      await dispatch('moveDayFlag', {
        prevRouteKey: connector.prevRouteKey,
        nextRouteKey: connector.nextRouteKey,
        targetCoord,
        destination,
        allowDetour: true,
      });
    }

    squash();
    await dispatch('refreshHighlights');
    await dispatch('updateRouteAndMarkers');
    return true;
  },
};
